import type { QueryClient } from "@tanstack/react-query";

type ClosedPositionRealizedTransfer = {
  readonly accountId: unknown;
  readonly realizedPnl: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toRecord = (value: unknown): Record<string, unknown> | null =>
  isRecord(value) ? value : null;

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const preserveNumberShape = (current: unknown, next: number): number | string =>
  typeof current === "string" ? String(next) : next;

const patchAccountRow = (
  value: unknown,
  accountId: number,
  realizedDelta: number,
): unknown => {
  const account = toRecord(value);
  if (!account || account.id !== accountId) return value;

  const rawRealized = toFiniteNumber(account.realized_pnl);
  if (rawRealized !== null) {
    return {
      ...account,
      realized_pnl: preserveNumberShape(
        account.realized_pnl,
        rawRealized + realizedDelta,
      ),
    };
  }

  const adaptedRealized = toFiniteNumber(account.realizedPnl);
  if (adaptedRealized !== null) {
    return {
      ...account,
      realizedPnl: preserveNumberShape(
        account.realizedPnl,
        adaptedRealized + realizedDelta,
      ),
    };
  }

  const balance = toRecord(account.balance);
  const balanceRealized = toFiniteNumber(balance?.realizedPnl);
  if (!balance || balanceRealized === null) return value;
  return {
    ...account,
    balance: {
      ...balance,
      realizedPnl: preserveNumberShape(
        balance.realizedPnl,
        balanceRealized + realizedDelta,
      ),
    },
  };
};

export const transferClosedPositionRealizedPnl = (
  queryClient: QueryClient,
  transfer: ClosedPositionRealizedTransfer,
): boolean => {
  if (
    typeof transfer.accountId !== "number" ||
    !Number.isFinite(transfer.accountId)
  ) {
    return false;
  }
  const accountId = transfer.accountId;
  const realizedDelta = toFiniteNumber(transfer.realizedPnl);
  if (realizedDelta === null) return false;

  let patched = false;
  queryClient.setQueriesData(
    { queryKey: ["accounts"] },
    (oldData: unknown): unknown => {
      const cached = toRecord(oldData);
      if (!cached) return oldData;
      let cachePatched = false;

      const patchRows = (rows: readonly unknown[]): unknown[] =>
        rows.map((row) => {
          const next = patchAccountRow(
            row,
            accountId,
            realizedDelta,
          );
          if (next !== row) {
            cachePatched = true;
            patched = true;
          }
          return next;
        });

      const next: Record<string, unknown> = { ...cached };
      if (Array.isArray(cached.items)) next.items = patchRows(cached.items);
      if (Array.isArray(cached.accounts)) {
        next.accounts = patchRows(cached.accounts);
      }
      const results = toRecord(cached.results);
      if (results && Array.isArray(results.accounts)) {
        next.results = {
          ...results,
          accounts: patchRows(results.accounts),
        };
      }
      return cachePatched ? next : oldData;
    },
  );
  return patched;
};
